export const DATE_MASK = "__/__/____";

/* Formats an ISO date string (YYYY-MM-DD) to DD/MM/YYYY */
export function formatDate(isoString) {
  const date = parseDateOnly(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/* Formats a date range given two date strings in the form of 'YYYY-MM-DD' */
export function formatDateRange(startIso, endIso) {
  return `(${formatDate(startIso)} - ${formatDate(endIso)})`;
}

/* Splits trips into upcoming and past based on current date */
export function splitTripsByDate(trips) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = trips
    .filter(t => parseDateOnly(t.toDate) >= today)
    .sort((a, b) => parseDateOnly(a.fromDate) - parseDateOnly(b.fromDate));

  const past = trips
    .filter(t => parseDateOnly(t.toDate) < today)
    .sort((a, b) => parseDateOnly(b.toDate) - parseDateOnly(a.toDate));

  return { upcoming, past };
};

/* Extracts digits from a masked date input (DD/MM/YYYY) */
export function extractDigits(maskedValue) {
  return (maskedValue || "").replace(/\D/g, "").slice(0, 8);
}

/* Creates a masked date display (DD/MM/YYYY) from digits */
export function createMaskedDisplay(digits) {
  const padded = (digits + "________").slice(0, 8);
  const day = padded.slice(0, 2);
  const month = padded.slice(2, 4);
  const year = padded.slice(4);
  return `${day}/${month}/${year}`;
}

/* Validates and builds a Date object from digits */
export function buildDateFromDigits(digits) {

  // Don’t validate until we have 8 digits
  if (digits.length !== 8) return { date: null, error: "" };

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));

  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return {
      date: null,
      error: "Please enter a valid date in DD/MM/YYYY format.",
    };
  }

  if (day < 1 || day > 31) {
    return { date: null, error: "Please enter a valid day (1–31)." };
  }

  if (month < 1 || month > 12) {
    return { date: null, error: "Please enter a valid month (1–12)." };
  }

  const currentYear = new Date().getFullYear();

  if (year < 1900) {
    return {
      date: null,
      error: "Are you traveling back to the past 😵",
    };
  }

  if (year > currentYear + 100) {
    return {
      date: null,
      error: "Cannot plan 100+ years ahead can we 🫢",
    };
  }

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return {
      date: null,
      error: "Please enter a real calendar date.",
    };
  }

  return { date, error: "" };
}

/* Maps number of typed digits → cursor position in the DD/MM/YYYY masked string */
export function digitsToCursorPos(numDigits) {
  if (numDigits < 2) return numDigits;
  if (numDigits === 2) return 3;          // jump past the first /
  if (numDigits < 4) return numDigits + 1;
  if (numDigits === 4) return 6;          // jump past the second /
  return numDigits + 2;
}

/* Slot index (0–7) → cursor position inside "DD/MM/YYYY" (length 10) */
const SLOT_TO_MASK = [0, 1, 3, 4, 6, 7, 8, 9];
export function slotToMaskPos(slotIdx) {
  return slotIdx >= 8 ? 10 : SLOT_TO_MASK[slotIdx];
}

/* Cursor position → digit slot to fill when typing forward (null = past end) */
export function maskPosToSlotForward(maskPos) {
  // DD/MM/YYYY — slashes at positions 2 and 5
  const map = [0, 1, 2, 2, 3, 4, 4, 5, 6, 7];
  return maskPos < map.length ? map[maskPos] : null;
}

/* Cursor position → digit slot to clear on Backspace (null = nothing before) */
export function maskPosToSlotBackward(maskPos) {
  const map = [null, 0, 1, 1, 2, 3, 3, 4, 5, 6, 7];
  return maskPos < map.length ? map[maskPos] : null;
}

export function formatDigits(d) {
  return d ? [
    d.getDate().toString().padStart(2, '0'),
    (d.getMonth() + 1).toString().padStart(2, '0'),
    d.getFullYear().toString().padStart(4, '0')
  ].join('') : '';
}

/**
 * field: "from" | "to"
 * digits: the digits for THIS field
 * otherDigits: the digits for the other field
 */
export function validateDates(
  field,
  digits,
  otherDigits,
  setFromDateError,
  setToDateError,
  setDurationInDays,
  setMissingFromDate,
  setMissingToDate
) {
  const isFrom = field === "from";
  const setThisError = isFrom ? setFromDateError : setToDateError;
  const setOtherError = isFrom ? setToDateError : setFromDateError;

  const thisResult = digits
    ? buildDateFromDigits(digits)
    : { date: null, error: "" };

  const otherResult = otherDigits
    ? buildDateFromDigits(otherDigits)
    : { date: null, error: "" };

  if (thisResult.error) {
    setThisError(thisResult.error);
    return;
  }

  if (otherResult.error) {
    setOtherError(otherResult.error);
    setThisError("");
    return;
  }

  // If this field is valid and complete, but the other is empty/incomplete, clear this field's error.
  if (thisResult.date && !otherResult.date) {
    setThisError("");
    return;
  }

  if (thisResult.date && otherResult.date) {
    if (isFrom && thisResult.date > otherResult.date) {
      setThisError('"From" date must be earlier than "To" date.');
      setOtherError("");
    } else if (!isFrom && thisResult.date < otherResult.date) {
      setThisError('"To" date must be later than "From" date.');
      setOtherError("");
    } else {
      setFromDateError("");
      setToDateError("");

      // Both dates valid and ordered: update duration inclusive of both endpoints
      setDurationInDays(calculateDurationInDays(
        isFrom ? digits : otherDigits,
        isFrom ? otherDigits : digits
      ));
      setMissingFromDate(false);
      setMissingToDate(false);
      return;
    }
  } else {
    setThisError("");
  }

  // If we reach here, duration is not valid; clear it
  setDurationInDays(null);
}

/* Calculates the duration in days between two dates */
export function calculateDurationInDays(fromDigits, toDigits) {

  const fromResult = buildDateFromDigits(fromDigits);
  const toResult = buildDateFromDigits(toDigits);

  if (fromResult.date && toResult.date) {
    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = (toResult.date - fromResult.date) / msPerDay;
    return Math.floor(diff) + 1; // inclusive of both days
  }
}

/* Convert "HH:mm AM/PM" string to a Date object for the picker */
export const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);
  return date;
};

/* --- Time conversion --- */
export const parseTime = (value) => {
  if (!value) return { hour: '--', minute: '--', ampm: '--' };

  // Split "h:mm ampm" into ["h:mm", "ampm"]
  const parts = value.split(' ');
  const timePart = parts[0];
  const ampm = parts[1] || '--';

  // Split "h:mm" into ["h", "mm"]
  if (!timePart.includes(':')) return { hour: '--', minute: '--', ampm: '--' };
  const [h, m] = timePart.split(':');

  return {
    hour: h.padStart(2, '0'),
    minute: m.padStart(2, '0'),
    ampm
  };
};

/* Calculate duration in days from two Date objects */
export function calculateDurationFromDates(fromDate, toDate) {
  if (!fromDate || !toDate) return null;
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = (toDate - fromDate) / msPerDay;
  return Math.floor(diff) + 1; // +1 because trips are inclusive
}

/* Parse date in the form of 'YYYY-MM-DD' (or any ISO) - a LOCAL-midnight Date (no UTC shift) */
export function parseDateOnly(str) {
  if (!str) return null;
  const [y, m, d] = String(str).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/* Format a Date in the form of 'YYYY-MM-DD' using LOCAL calendar parts (never toISOString) */
export function formatDateOnly(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
